<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Direct Order</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="'Filter Current Page'"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Advanced Search</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateCreateOn"
                            :from-label="formLabelText.dateCreateOnFrom"
                            :to-label="formLabelText.dateCreateOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilters.directOrderNumber"
                                    label="Direct Order Number"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilters.trackingNumber"
                                    label="Tracking Number"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilters.customerReference"
                                    label="Customer Reference"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="6">
                                <v-select
                                    v-model="data.searchFilters.directOrderStatus"
                                    :items="directOrderStatusOptions"
                                    item-title="text"
                                    item-value="value"
                                    label="Direct Order Status"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                                <v-select
                                    v-model="data.searchFilters.directOrderCourierCode"
                                    :items="courierList"
                                    label="Courier Code"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6">
                            <v-btn class="mr-2" text @click="onSearchClick">
                                <v-icon>mdi-cached</v-icon>
                                Refresh
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" color="warning" @click="onBulkUpdateLastMileClick">
                                Bulk update Last mile
                            </v-btn>
                            <export-to-csv
                                :data="currentPageData"
                                :csv-header="data.csvExportConfig.csvHeader"
                                :filename="data.csvExportConfig.filename"
                                :formatter="csvFormatter"
                            ></export-to-csv>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'directOrderNumber'"
                        :action-header-value="directOrderActionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more direct orders...'"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions>
                            <!-- No actions for operation portal -->
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted, computed } from 'vue';
import * as _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DirectOrderLabelTextHelper } from '../../utils/labelTextHelper/DirectOrderLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import { DirectOrderHeaderHelper } from '../../utils/DirectOrderHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import GenericListTable from '../../components/GenericListTable.vue';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import { usePagination } from '../../composables/usePagination';
import { useMasterData } from '../../composables/useMasterData';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

export default defineComponent({
    name: 'ListDirectOrder',

    components: {
        ExportToCsv,
        Datepicker,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFiltersDefault: {},
            searchFilters: {
                directOrderNumber: null,
                trackingNumber: null,
                directOrderStatus: null,
                directOrderCourierCode: null,
                customerReference: null
            },
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateCreateOn: {
                    from: null,
                    to: null
                }
            },
            csvExportConfig: {
                filename: 'exportDirectOrder.csv',
                csvHeader: [...DirectOrderHeaderHelper.getCsvHeader()]
            },
            validation: {
                hasErrors: false
            }
        });

        // Use master data composable
        const { directOrderStatus, courierList, loadAllMasterData } = useMasterData();

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            initialize
        } = usePagination({
            apiEndpoint: '/api/operation/directOrder/list',
            buildApiParams: () => {
                const params: any = {};

                // Text filters
                if (data.searchFilters.directOrderNumber?.trim()) {
                    params.directOrderNumber = data.searchFilters.directOrderNumber.trim();
                }
                if (data.searchFilters.trackingNumber?.trim()) {
                    params.trackingNumber = data.searchFilters.trackingNumber.trim();
                }
                if (data.searchFilters.customerReference?.trim()) {
                    params.customerReference = data.searchFilters.customerReference.trim();
                }
                if (data.searchFilters.directOrderStatus?.trim()) {
                    params.directOrderStatus = data.searchFilters.directOrderStatus.trim();
                }
                if (data.searchFilters.directOrderCourierCode?.trim()) {
                    params.directOrderCourierCode = data.searchFilters.directOrderCourierCode.trim();
                }

                // Add date range filtering if specified
                if (data.searchFilterDateRangeGrp.dateCreateOn.from) {
                    params.dateCreateOnFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateCreateOn.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateCreateOn.to) {
                    params.dateCreateOnTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateCreateOn.to
                    );
                }

                return params;
            }
        });

        onMounted(async () => {
            data.searchFiltersDefault = _.cloneDeep(data.searchFilters);
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);
            // Load master data using composable (cached across components)
            await loadAllMasterData();
            await initialize();
        });

        // Convert directOrderStatus object to array for v-select
        const directOrderStatusOptions = computed(() => {
            if (!directOrderStatus.value || Object.keys(directOrderStatus.value).length === 0) {
                return [];
            }
            return Object.entries(directOrderStatus.value).map(([key, value]) => ({
                text: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
                value: value
            }));
        });

        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: DirectOrderLabelTextHelper.getLabelText,
            directOrderStatus,
            directOrderStatusOptions,
            courierList,
            directOrderActionHeaderItem: DirectOrderHeaderHelper.getActionHeaderItem(),
            headers: [...DirectOrderHeaderHelper.getHeaders()],
            data,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilters = _.cloneDeep(this.data.searchFiltersDefault);
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.$refs.errorAlert.showAlert();
                return;
            }

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onBulkUpdateLastMileClick() {
            await this.$router.push({ path: PathDefinition.importUpdateLastMileReference });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        // Pagination event handlers
        async onNextPage() {
            await this.nextPage();
        },

        onPreviousPage() {
            this.previousPage();
        },

        onPageSelect(selectedPage: number) {
            this.selectPage(selectedPage);
        },

        async onPageSizeChange() {
            await this.changePageSize();
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>

